import { useState } from 'react';
import '../tailwind.css';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Reporting/ui/card';
import ReportForm from '../../../components/Reporting/report-form';
import ReportOutput from '../../../components/Reporting/report-output';
import PosPerformanceOutput from '../../../components/Reporting/pos-performance-output';
import { HeaderMenu } from '../../../components/Reporting/header-menu';
import instance from '../../../services/api';
import { toast } from 'react-toastify';
import ThermalPrintView from 'components/Reporting/thermal-print-view';
import PagePrintPreview from 'components/Reporting/page-print-preview';

export default function ReportGenerator() {
  const [reportData, setReportData] = useState(null);
  const [include, setInclude] = useState(undefined);
  const [tenderTypes, setTenderTypes] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [show80mmView, setShow80mmView] = useState(false);
  const [showPagePreview, setShowPagePreview] = useState(false);

  const handlePrint = () => {
    setShowPagePreview(true);
  };

  const handle80mmPrint = () => {
    setShow80mmView(true);
  };

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await instance.post('/reports/pos-performance', data);
      setReportData(response.data);
      setInclude(data.include);
      setTenderTypes(data.tenderTypes);
      toast.success('Report Generated Successfully');

      // Scroll to report output
      setTimeout(() => {
        document.getElementById('report-output')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        'Failed to generate report. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderMenu
        currentPage="comprehensive"
        reportData={reportData}
        onPrint={handlePrint}
        on80mmPrint={handle80mmPrint}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Report Form */}
        <Card className="mb-8 no-print">
          <CardHeader>
            <CardTitle className="flex items-center">
              <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
              Report Parameters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportForm
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              setReportData={setReportData}
              showPaymentMethods
              excludedIncludeSections={['driverBreakdown']}
            />
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Card className="mb-8">
            <CardContent className="pt-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                <span className="text-foreground">Generating report...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card className="mb-8 border-destructive bg-destructive/10">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-destructive rounded-full mr-3"></div>
                <div>
                  <h3 className="text-lg font-medium text-destructive">
                    Error Generating Report
                  </h3>
                  <p className="text-destructive/80 mt-1">
                    {error?.message ||
                      'Unable to fetch report data. Please check your parameters and try again.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Report Output */}
        {reportData && (
          <div id="report-output">
            {/* Use PosPerformanceOutput for new API structure */}
            {reportData.todaysTotalOrders !== undefined ? (
              <PosPerformanceOutput
                data={reportData}
                include={include}
                tenderTypes={tenderTypes}
              />
            ) : (
              <ReportOutput data={reportData} />
            )}
          </div>
        )}
      </main>

      {/* 80mm Thermal Print View */}
      {show80mmView && reportData && (
        <ThermalPrintView
          data={reportData}
          tenderTypes={tenderTypes}
          onClose={() => setShow80mmView(false)}
        />
      )}

      {/* Full Page Print Preview */}
      {showPagePreview && reportData && (
        <PagePrintPreview
          data={reportData}
          contentSelector="#report-output"
          onClose={() => setShowPagePreview(false)}
          reportType="POS-Performance"
        />
      )}
    </div>
  );
}
